/**
 * Data access for signed containers (.asice/.bdoc/.ddoc).
 *
 * A row in any document view is signature-relevant in one of two ways: it IS a
 * signed container, or it is a document enclosed in one (the pipeline unzips a
 * container into children that carry `parent_document_id`). Both resolve to the
 * same `SignedContainerInfo`, which is why every view shares this module.
 */

import { supabase } from "@/integrations/supabase/client";
import { isSignedContainerFile } from "../constants/fileTypes";
import {
  deriveSignatureStatus,
  type ContainerSignature,
  type EnclosedDocument,
  type SignedContainerInfo,
} from "./signatureModel";

interface SignatureRow {
  id: number;
  document_id: number;
  signer_name: string;
  signer_country: string;
  method: string;
  signature_type: string | null;
  signed_at: string;
}

/**
 * Deliberately WITHOUT `signer_personal_code`: the national identity number is
 * not granted to `authenticated`, so naming it here makes the whole query fail
 * with 42501 rather than returning the rest of the row. Both signature queries
 * share this list so they cannot drift apart.
 */
const SIGNATURE_COLUMNS =
  "id, document_id, signer_name, signer_country, method, signature_type, signed_at";

interface ContainerRow {
  id: number;
  filename: string;
  size: number | null;
}

const toContainerSignature = (row: SignatureRow): ContainerSignature => ({
  id: row.id,
  signerName: row.signer_name,
  signerCountry: row.signer_country,
  method: row.method,
  signatureType: row.signature_type ?? null,
  signedAt: row.signed_at,
  // Franklin records the signing event but does not (yet) re-validate the
  // container against the EU trust list, so validity stays unknown and the
  // UI renders a neutral state instead of a green tick.
  validity: "unknown",
});

/** Signatures recorded for a container, oldest first. */
export async function fetchContainerSignatures(containerId: string): Promise<ContainerSignature[]> {
  const { data, error } = await supabase
    .from("document_signatures")
    .select(SIGNATURE_COLUMNS)
    .eq("document_id", Number(containerId))
    .order("signed_at", { ascending: true });

  if (error) throw new Error(error.message);

  return (data ?? []).map(toContainerSignature);
}

interface PendingContainerInfo {
  resolve: (info: SignedContainerInfo | null) => void;
  reject: (error: unknown) => void;
}

let pendingContainerInfo = new Map<string, PendingContainerInfo[]>();
let containerInfoFlushScheduled = false;

/**
 * Resolve a document id to signed-container info, or `null` when that document
 * is not a signed container (a plain `.zip`, or a regular file whose parent id
 * was passed in). Concurrent row requests are coalesced into one documents
 * query and one signatures query, so rendering a page of containers does not
 * create two sequential requests per row.
 */
export function fetchSignedContainerInfo(containerId: string): Promise<SignedContainerInfo | null> {
  return new Promise((resolve, reject) => {
    const waiters = pendingContainerInfo.get(containerId) ?? [];
    waiters.push({ resolve, reject });
    pendingContainerInfo.set(containerId, waiters);

    if (!containerInfoFlushScheduled) {
      containerInfoFlushScheduled = true;
      queueMicrotask(() => {
        void flushContainerInfoRequests();
      });
    }
  });
}

async function flushContainerInfoRequests(): Promise<void> {
  const batch = pendingContainerInfo;
  pendingContainerInfo = new Map();
  containerInfoFlushScheduled = false;

  try {
    const infos = await fetchSignedContainerInfoBatch([...batch.keys()]);
    for (const [id, waiters] of batch) {
      const info = infos.get(id) ?? null;
      waiters.forEach(({ resolve }) => resolve(info));
    }
  } catch (error) {
    for (const waiters of batch.values()) {
      waiters.forEach(({ reject }) => reject(error));
    }
  }
}

const normalizeContainerIds = (containerIds: string[]): number[] => [
  ...new Set(containerIds.map(Number).filter((id) => Number.isSafeInteger(id) && id > 0)),
];

async function fetchSignedContainerRows(containerIds: number[]): Promise<ContainerRow[]> {
  const { data, error } = await supabase
    .from("documents")
    .select("id, filename, size")
    .in("id", containerIds);

  if (error) throw new Error(error.message);
  return (data ?? []).filter(
    (row: ContainerRow) => isSignedContainerFile(row.filename) || /\.pdf$/i.test(row.filename)
  );
}

async function fetchSignaturesByContainer(
  containers: ContainerRow[]
): Promise<Map<number, ContainerSignature[]>> {
  const { data, error } = await supabase
    .from("document_signatures")
    .select(SIGNATURE_COLUMNS)
    .in(
      "document_id",
      containers.map((row) => row.id)
    )
    .order("signed_at", { ascending: true });
  if (error) throw new Error(error.message);

  const signaturesByContainer = new Map<number, ContainerSignature[]>();
  for (const row of data ?? []) {
    const signatures = signaturesByContainer.get(row.document_id) ?? [];
    signatures.push(toContainerSignature(row));
    signaturesByContainer.set(row.document_id, signatures);
  }
  return signaturesByContainer;
}

function toSignedContainerInfoMap(
  containers: ContainerRow[],
  signaturesByContainer: Map<number, ContainerSignature[]>
): Map<string, SignedContainerInfo> {
  return new Map(
    containers.map((container) => {
      const signatures = signaturesByContainer.get(container.id) ?? [];
      return [
        String(container.id),
        {
          containerId: String(container.id),
          containerFilename: container.filename,
          containerSize: container.size ?? null,
          signatures,
          status: deriveSignatureStatus(signatures),
        },
      ];
    })
  );
}

async function fetchSignedContainerInfoBatch(
  containerIds: string[]
): Promise<Map<string, SignedContainerInfo>> {
  const numericIds = normalizeContainerIds(containerIds);
  if (numericIds.length === 0) return new Map();

  const containers = await fetchSignedContainerRows(numericIds);
  if (containers.length === 0) return new Map();

  const signaturesByContainer = await fetchSignaturesByContainer(containers);
  return toSignedContainerInfoMap(containers, signaturesByContainer);
}

interface EnclosedRow {
  id: number;
  filename: string;
  size: number | null;
  content_type: string | null;
  file_path: string | null;
  building_id: number | null;
  created_at: string | null;
  state: string | null;
}

/** The columns `loadDocumentPreview` needs, so the panel can open a document
 *  the surrounding list never holds a row for. */
const ENCLOSED_COLUMNS =
  "id, filename, size, content_type, file_path, building_id, created_at, state";

/** SQL NULL and an absent column both read as `null` — never `undefined`, and
 *  never the string "undefined" once an id is stringified. */
const orNull = <T>(value: T | null | undefined): T | null => value ?? null;
const idOrNull = (value: number | null | undefined): string | null =>
  value == null ? null : String(value);

const toEnclosedDocument = (row: EnclosedRow): EnclosedDocument => ({
  id: String(row.id),
  filename: row.filename,
  size: orNull(row.size),
  contentType: orNull(row.content_type),
  filePath: orNull(row.file_path),
  buildingId: idOrNull(row.building_id),
  createdAt: orNull(row.created_at),
  state: orNull(row.state),
});

/**
 * The documents a container encloses. Franklin fills that list two different
 * ways and a container row must show its contents either way:
 *
 * - **Signed in Franklin** — the Dokobit flow stores the container terminal and
 *   `do_not_unzip`, so it has NO child rows; what went into it is recorded in
 *   `signed_container_sources` (the originals stay in the document list).
 * - **Uploaded from outside** — the pipeline unzips it, so its contents are
 *   ordinary child rows carrying `parent_document_id`.
 *
 * Both are merged (a document can legitimately be both a source and a child),
 * deduped by id and sorted by filename.
 */
export async function fetchEnclosedDocuments(containerId: string): Promise<EnclosedDocument[]> {
  const numericId = Number(containerId);

  const [children, sources] = await Promise.all([
    fetchContainerChildren(numericId),
    fetchSignedContainerSources(numericId),
  ]);

  const byId = new Map<string, EnclosedDocument>();
  for (const doc of [...children, ...sources]) byId.set(doc.id, doc);

  return [...byId.values()].sort((a, b) => a.filename.localeCompare(b.filename));
}

/** Unzipped children of an externally-uploaded container. */
async function fetchContainerChildren(containerId: number): Promise<EnclosedDocument[]> {
  const { data, error } = await supabase
    .from("documents")
    .select(ENCLOSED_COLUMNS)
    .eq("parent_document_id", containerId)
    .eq("is_deleted", false);

  if (error) throw new Error(error.message);
  return (data ?? []).map(toEnclosedDocument);
}

/** The documents that were signed into a Franklin-produced container. */
async function fetchSignedContainerSources(containerId: number): Promise<EnclosedDocument[]> {
  const { data: links, error: linkError } = await supabase
    .from("signed_container_sources")
    .select("source_document_id")
    .eq("container_document_id", containerId);

  if (linkError) throw new Error(linkError.message);

  const sourceIds = (links ?? []).map((row) => row.source_document_id);
  if (sourceIds.length === 0) return [];

  const { data, error } = await supabase
    .from("documents")
    .select(ENCLOSED_COLUMNS)
    .in("id", sourceIds)
    .eq("is_deleted", false);

  if (error) throw new Error(error.message);
  return (data ?? []).map(toEnclosedDocument);
}
